import Box from "@mui/material/Box";
import { styled, keyframes } from "@mui/material/styles";

import * as React from "react";
import { StoneControl } from "../../controls/StoneControl";
import { Game } from "../../models";
import { BoardField } from "../GameControl";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import { useTheme as useAppTheme } from "../../contexts/ThemeContext";
import {
  Card,
  CardContent,
  IconButton,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Stack,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import { getGameConfig } from "../../utils/gameConfig";
import { useTranslation } from "react-i18next";
import SchoolIcon from "@mui/icons-material/School";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { EditGameFormControl } from "./EditGameFormControl";
import { useNavigate } from "react-router-dom";
import { createRematch } from "../../utils/common";
import {
  boardOnlyWrapperSx,
  gameCardSx,
  cardContentSx,
  headerContainerSx,
  playerInfoContainerSx,
  playerNameSx,
  playerSubtitleSx,
  headerTitleContainerSx,
  headerTitleSx,
  infoButtonSx,
  currentStoneContainerSx,
  currentStoneBoxSx,
  currentStoneLabelSx,
  dialogPaperSx,
  dialogTitleSx,
  dialogCloseButtonSx,
  dialogContentSx,
} from "./gameBoardStyles";

interface GameBoardControlProps {
  game: Game;
  setGame?: (game: Game) => void;
  myTurn?: boolean;
  player?: number;
  stone?: any;
  placeStone?: (index: number) => Promise<void>;
  winRow?: number[] | undefined;
  sx?: any;
  updateGame?: (data: {
    title?: string;
    description?: string;
    iconSet?: number;
    expertMode?: boolean;
  }) => void;
  emptyBoard?: boolean;
  boardOnly?: boolean;
}

// Animations
const winningGlow = keyframes`
  0% {
    box-shadow: 
      inset 0 2px 6px rgba(255,215,0,0.5),
      0 4px 15px rgba(255,215,0,0.6),
      0 0 25px rgba(255,215,0,0.9),
      0 0 45px rgba(255,165,0,0.7);
    transform: scale(1);
  }
  50% {
    box-shadow: 
      inset 0 2px 8px rgba(255,215,0,0.7),
      0 4px 25px rgba(255,215,0,0.9),
      0 0 45px rgba(255,215,0,1),
      0 0 70px rgba(255,165,0,0.9),
      0 0 100px rgba(255,140,0,0.7);
    transform: scale(1.06);
  }
  100% {
    box-shadow: 
      inset 0 2px 6px rgba(255,215,0,0.5),
      0 4px 15px rgba(255,215,0,0.6),
      0 0 25px rgba(255,215,0,0.9),
      0 0 45px rgba(255,165,0,0.7);
    transform: scale(1);
  }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
`;

const stonePreviewAnimation = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0.4;
  }
`;

const trophyBounce = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-100px) rotate(-180deg) scale(0);
  }
  60% {
    transform: translateY(5px) rotate(10deg) scale(1.1);
  }
  80% {
    transform: translateY(-3px) rotate(-5deg) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) rotate(0deg) scale(1);
  }
`;

const trophyGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 8px rgba(255,215,0,0.7));
  }
  50% {
    filter: drop-shadow(0 0 20px rgba(255,215,0,1));
  }
`;

const textFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Base GameBoard - styling will be applied via sx prop
const GameBoard = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    gap: 6,
    width: "320px",
    height: "320px",
  },
  [theme.breakpoints.up("sm")]: {
    gap: 8,
    width: "480px",
    height: "480px",
  },
  gridTemplateColumns: "repeat(4, 1fr)",
  gridTemplateRows: "repeat(4, 1fr)",
  display: "grid",
  padding: theme.spacing(3),
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: -4,
    zIndex: -1,
  },
}));

// Base Tile - styling will be applied via sx prop
export const Tile = styled("div")(({ theme }) => ({
  borderRadius: theme.spacing(1),
  textAlign: "center",
  fontSize: "0.875rem",
  fontWeight: "700",
  transition: "all 0.25s ease-out",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
}));

// Style helpers to keep JSX clean & readable
const getWinningTileStyle = (
  themeConfig: any,
  index: number,
  winRow?: number[]
) => {
  if (!winRow || winRow.indexOf(index) === -1) return {};
  return {
    background: themeConfig.tile.winningBackground,
    border: themeConfig.tile.winningBorder,
    animation: `${winningGlow} 1.8s ease-in-out infinite`,
  };
};

const gameBoardSx = (themeConfig: any) => ({
  background: themeConfig.board.background,
  border: themeConfig.board.border,
  borderRadius: themeConfig.board.borderRadius,
  boxShadow: themeConfig.board.boxShadow,
  "&::before": {
    background: themeConfig.board.beforeBackground,
    borderRadius: themeConfig.board.borderRadius,
  },
});

const errorTileSx = (themeConfig: any) => ({
  border: "3px solid",
  borderColor: "error.main",
  background: themeConfig.tile.background,
  boxShadow: themeConfig.tile.boxShadow,
  cursor: "not-allowed",
});

const confirmTileSx = (themeConfig: any) => ({
  border: "3px solid",
  borderColor: "warning.main",
  background: themeConfig.tile.background,
  boxShadow: `0 0 20px ${themeConfig.accent.glow}`,
});

const confirmButtonSx = (colorKey: "success" | "error") => ({
  bgcolor: `${colorKey}.main`,
  color: "white",
  "&:hover": { bgcolor: `${colorKey}.dark` },
});

const interactiveTileSx = (
  themeConfig: any,
  isClickable: boolean,
  isOccupied: boolean,
  winningTileStyle: any
) => ({
  background: themeConfig.tile.background,
  border: themeConfig.tile.border,
  boxShadow: themeConfig.tile.boxShadow,
  cursor: isClickable && !isOccupied ? "pointer" : "default",
  "&:hover": {
    transform: isClickable && !isOccupied ? "scale(1.05)" : "none",
    boxShadow:
      isClickable && !isOccupied
        ? "0 6px 20px rgba(0,0,0,0.3)"
        : themeConfig.tile.boxShadow,
  },
  ...winningTileStyle,
});

const stonePreviewBoxSx = {
  opacity: "40%",
  width: "100%",
  height: "100%",
  animation: `${stonePreviewAnimation} 0.3s ease-out`,
};

const winnerContainerSx = {
  width: "100%",
  mb: 4,
  pb: 3,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: 1.5,
  borderBottom: "1px solid",
  borderColor: "divider",
} as const;

const trophyBadgeSx = (themeConfig: any) => ({
  display: "inline-flex",
  width: { xs: 64, sm: 72 },
  height: { xs: 64, sm: 72 },
  bgcolor: "primary.main",
  borderRadius: "50%",
  alignItems: "center",
  justifyContent: "center",
  border: "3px solid",
  borderColor: "warning.light",
  boxShadow: `0 0 25px ${themeConfig.accent.glow}`,
  animation: `${trophyBounce} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)`,
});

const trophyIconSx = {
  fontSize: { xs: 36, sm: 42 },
  color: "primary.contrastText",
  animation: `${trophyGlow} 2s ease-in-out infinite`,
} as const;

const winnerNameSx = {
  fontSize: { xs: "1.6rem", sm: "1.9rem" },
  letterSpacing: 0.5,
  animation: `${textFadeIn} 0.5s ease-out 0.3s both`,
} as const;

const winnerSubtitleSx = {
  mt: 0.25,
  fontSize: { xs: "0.9rem", sm: "1rem" },
  color: "text.secondary",
  animation: `${textFadeIn} 0.5s ease-out 0.4s both`,
} as const;

const winReasonTextSx = (themeConfig: any) => ({
  mt: 1,
  px: 2,
  py: 0.5,
  bgcolor: themeConfig.accent.background,
  borderRadius: 999,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.8rem",
  textTransform: "uppercase",
  letterSpacing: 0.8,
  animation: `${textFadeIn} 0.5s ease-out 0.5s both`,
});

const rematchContainerSx = {
  mt: 3,
  width: "100%",
  display: "flex",
  justifyContent: "center",
  animation: `${textFadeIn} 0.5s ease-out 0.6s both`,
} as const;

const rematchButtonSx = {
  py: 1.4,
  px: 5,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  fontWeight: 700,
  "&:hover": {
    bgcolor: "primary.dark",
    transform: "scale(1.05)",
  },
  transition: "all 0.2s",
} as const;

const joinRematchButtonSx = {
  py: 1.4,
  px: 5,
  bgcolor: "success.main",
  color: "success.contrastText",
  fontWeight: 700,
  "&:hover": {
    bgcolor: "success.dark",
    transform: "scale(1.05)",
  },
  transition: "all 0.2s",
} as const;

export function GameBoardControl(props: GameBoardControlProps) {
  const { SHOW_CONFIRM_BUTTONS } = getGameConfig();
  const { t } = useTranslation();
  const { themeConfig } = useAppTheme();
  const navigate = useNavigate();
  const { boardOnly = false, emptyBoard = false } = props;
  const [hoverKey, setHoverKey] = React.useState<string>();
  const [errorKey, setErrorKey] = React.useState<string>();
  const [confirmKey, setConfirmKey] = React.useState<string>();
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const { game, myTurn, placeStone, winRow, sx, stone, updateGame, player } =
    props;

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const winReasons = React.useMemo(() => {
    if (!winRow || !game.board) return [];
    const stones = winRow.map((idx) => game.board![idx]?.stone).filter(Boolean);
    if (stones.length !== 4) return [];

    const reasons: string[] = [];
    const checkAttr = (
      attr: "color" | "size" | "shape" | "variant",
      label: string
    ) => {
      const values = stones.map((s) => s![attr]);
      const unique = new Set(values).size;
      if (unique === 1) reasons.push(t(`gameControl:${label}Same`));
      else if (unique === 4) reasons.push(t(`gameControl:${label}Different`));
    };

    checkAttr("color", "color");
    checkAttr("size", "size");
    checkAttr("shape", "shape");
    checkAttr("variant", "variant");

    return reasons;
  }, [game.board, winRow, t]);

  const renderTile = (field: BoardField, index: number) => {
    const tileKey = field.key?.toString();
    const hasStone = Boolean(field.stone?.key);
    const isMyTurn = Boolean(myTurn);

    const isError =
      isMyTurn && !!tileKey && errorKey != null && tileKey === errorKey;
    const isConfirm =
      isMyTurn &&
      !!tileKey &&
      confirmKey != null &&
      tileKey === confirmKey &&
      SHOW_CONFIRM_BUTTONS;
    const isHover =
      isMyTurn && !!tileKey && hoverKey != null && tileKey === hoverKey;

    const isClickable =
      isMyTurn && !!stone && !emptyBoard && !hasStone && !!tileKey;

    const winningTileStyle = getWinningTileStyle(themeConfig, index, winRow);

    const handleMouseEnter = () => {
      if (!isClickable) return;
      if (!tileKey) return;
      setHoverKey(tileKey);
    };

    const handleMouseLeave = () => {
      setHoverKey(undefined);
    };

    const handleClick = async () => {
      if (!isClickable || !tileKey) return;

      if (SHOW_CONFIRM_BUTTONS) {
        setConfirmKey(tileKey);
        return;
      }

      if (placeStone) {
        await placeStone(index);
      }
    };

    if (isError) {
      return (
        <Tile
          key={`tile-${index}`}
          onMouseLeave={() => {
            setErrorKey(undefined);
          }}
          sx={errorTileSx(themeConfig)}
        >
          <DoNotDisturbIcon
            sx={{
              fontSize: 40,
              color: "error.main",
              animation: `${shakeAnimation} 0.3s`,
            }}
          />
        </Tile>
      );
    }

    if (isConfirm) {
      return (
        <Tile key={`tile-${index}`} sx={confirmTileSx(themeConfig)}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              size="small"
              onClick={() => {
                if (placeStone) void placeStone(index);
                setConfirmKey(undefined);
              }}
              sx={confirmButtonSx("success")}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setConfirmKey(undefined)}
              sx={confirmButtonSx("error")}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Tile>
      );
    }

    const showPreview =
      isMyTurn && !!stone && !hasStone && isHover && !!game?.currentStone;

    return (
      <Tile
        key={`tile-${index}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        sx={interactiveTileSx(
          themeConfig,
          Boolean(isClickable),
          Boolean(hasStone),
          winningTileStyle
        )}
      >
        {hasStone && (
          <StoneControl
            stone={field.stone ?? undefined}
            iconSet={game?.iconSet}
          />
        )}
        {showPreview && (
          <Box sx={stonePreviewBoxSx}>
            <StoneControl
              stone={game.currentStone ?? undefined}
              iconSet={game?.iconSet}
              preview={true}
            />
          </Box>
        )}
      </Tile>
    );
  };

  // Render GameBoard Grid
  const renderGameBoard = () => {
    if (!game.board) return null;

    return (
      <GameBoard sx={gameBoardSx(themeConfig)}>
        {game.board.map((field, index) =>
          renderTile(field as BoardField, index)
        )}
      </GameBoard>
    );
  };

  // If boardOnly, just return the board grid
  if (boardOnly) {
    return <Box sx={boardOnlyWrapperSx(sx)}>{renderGameBoard()}</Box>;
  }

  const myPlayerName = player
    ? player === 1
      ? game.player1
      : game.player2
    : null;
  const iWon = myPlayerName != null && myPlayerName === game.winner;
  const winnerHeadline =
    game.winner != null
      ? iWon
        ? t("gameControl:youWon")
        : t("gameControl:opponentWon")
      : "";

  return (
    <Card elevation={0} sx={gameCardSx(themeConfig, sx)}>
      <CardContent sx={cardContentSx}>
        {/* Winner Header - Only show if there's a winner */}
        {game.winner && (
          <Box sx={winnerContainerSx}>
            {/* Trophy Badge */}
            <Box sx={trophyBadgeSx(themeConfig)}>
              <EmojiEventsIcon sx={trophyIconSx} />
            </Box>

            {/* Headline */}
            <Typography
              variant="h5"
              fontWeight={700}
              color="primary.main"
              sx={winnerNameSx}
            >
              {winnerHeadline}
            </Typography>

            {/* Reason */}
            {winReasons.length > 0 && (
              <Typography
                variant="body2"
                color="primary.main"
                sx={winReasonTextSx(themeConfig)}
              >
                {winReasons.join(" • ")}
              </Typography>
            )}

            {/* Restart Button */}
            <Box
              sx={{
                ...rematchContainerSx,
                mt: 2,
              }}
            >
              {!game.rematchId ? (
                <Button
                  onClick={() => createRematch(game, navigate)}
                  variant="contained"
                  size="large"
                  sx={rematchButtonSx}
                >
                  {t("gameControl:playAgainButton")}
                </Button>
              ) : (
                <Button
                  href={`/game/${game.rematchId}`}
                  variant="contained"
                  size="large"
                  sx={joinRematchButtonSx}
                >
                  {t("gameControl:joinRematchButton")}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {!game.winner && (
          <Box sx={headerContainerSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end", // unten bündig
                justifyContent: "space-between",
                width: "100%",
                gap: 2,
              }}
            >
              {/* Links: Spielerinfo (klein) */}
              <Box
                sx={{
                  ...playerInfoContainerSx,
                  flex: "0 0 auto",
                  maxWidth: 220,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={playerNameSx}
                  noWrap
                >
                  {game.currentPlayer === 1 ? game.player1 : game.player2}
                </Typography>
                <Typography variant="caption" sx={playerSubtitleSx} noWrap>
                  {!game.currentStone
                    ? myTurn
                      ? t("gameControl:opponentChoosesStone")
                      : t("gameControl:youChooseStone")
                    : myTurn
                    ? t("gameControl:yourTurn")
                    : t("gameControl:opponentsTurn")}
                </Typography>
              </Box>

              {/* Mitte: Titel, bekommt den meisten Platz */}
              <Box
                sx={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  gap: 1.5,
                  height: 80,
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={headerTitleSx}
                  noWrap
                >
                  {game.title}
                </Typography>
                {game.expertMode && (
                  <Tooltip title={t("gameControl:expertMode")}>
                    <SchoolIcon sx={{ fontSize: 24, color: "primary.main" }} />
                  </Tooltip>
                )}
                <Tooltip title={t("gameControl:gameInfo")}>
                  <IconButton
                    size="small"
                    onClick={() => setSettingsOpen(true)}
                    sx={infoButtonSx}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Rechts: aktueller Stein (klein) */}
              <Box
                sx={{
                  ...currentStoneContainerSx,
                  flex: "0 0 auto",
                  alignItems: "flex-end",
                }}
              >
                {stone ? (
                  <Stack alignItems="flex-end" >
                    <Tooltip title={t("Game.currentStone")}>
                      <Box sx={currentStoneBoxSx(themeConfig)}>
                        <StoneControl
                          stone={stone}
                          iconSet={game.iconSet}
                          size="small"
                        />
                      </Box>
                    </Tooltip>
                    <Typography
                      // display={{ xs: "none", md: "block" }}
                      variant="caption"
                      sx={currentStoneLabelSx}
                    >
                      {t("Game.currentStone")}
                    </Typography>
                  </Stack>
                ) : (
                  <Box sx={{ width: 60, height: 60 }} />
                )}
              </Box>
            </Box>
          </Box>
        )}
        {/* Minimal Header - Hide if winner */}
        {!game.winner && false && (
          <Box sx={headerContainerSx}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
                gridTemplateRows: "auto auto",
                columnGap: 2,
                rowGap: 1,
                width: "100%",
              }}
            >
              {/* Top row: centered title + info */}
              <Box
                sx={{
                  gridColumn: "1 / 4",
                  gridRow: "1 / 2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={headerTitleSx}
                  noWrap
                >
                  {game.title}
                </Typography>
                {game.expertMode && (
                  <Tooltip title={t("gameControl:expertMode")}>
                    <SchoolIcon sx={{ fontSize: 24, color: "primary.main" }} />
                  </Tooltip>
                )}
                <Tooltip title={t("gameControl:gameInfo")}>
                  <IconButton
                    size="small"
                    onClick={() => setSettingsOpen(true)}
                    sx={infoButtonSx}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Bottom left: player info */}
              <Box
                sx={{
                  gridColumn: "1 / 2",
                  gridRow: "2 / 3",
                  ...playerInfoContainerSx,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={playerNameSx}
                  noWrap
                >
                  {game.currentPlayer === 1 ? game.player1 : game.player2}
                </Typography>
                <Typography variant="caption" sx={playerSubtitleSx} noWrap>
                  {!game.currentStone
                    ? myTurn
                      ? t("gameControl:opponentChoosesStone")
                      : t("gameControl:youChooseStone")
                    : myTurn
                    ? t("gameControl:yourTurn")
                    : t("gameControl:opponentsTurn")}
                </Typography>
              </Box>

              {/* Bottom right: current stone */}
              <Box
                sx={{
                  gridColumn: "3 / 4",
                  gridRow: "2 / 3",
                  ...currentStoneContainerSx,
                  justifyContent: "flex-end",
                }}
              >
                {stone ? (
                  <>
                    <Tooltip title={t("Game.currentStone")}>
                      <Box sx={currentStoneBoxSx(themeConfig)}>
                        <StoneControl
                          stone={stone}
                          iconSet={game.iconSet}
                          size="small"
                        />
                      </Box>
                    </Tooltip>
                    <Typography
                      display={{ xs: "none", md: "block" }}
                      variant="caption"
                      sx={currentStoneLabelSx}
                    >
                      {t("Game.currentStone")}
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ width: 60, height: 60 }} /> // keep layout stable
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Settings Dialog */}
        <Dialog
          open={settingsOpen}
          onClose={handleSettingsClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: dialogPaperSx(themeConfig),
          }}
        >
          <DialogTitle sx={dialogTitleSx(themeConfig)}>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {t("gameControl:gameInformation")}
            </Typography>
            <IconButton onClick={handleSettingsClose} sx={dialogCloseButtonSx}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={dialogContentSx}>
            {updateGame && (
              <EditGameFormControl
                game={game}
                updateGame={updateGame}
                hideEdit={handleSettingsClose}
              />
            )}
          </DialogContent>
        </Dialog>

        {renderGameBoard()}
      </CardContent>
    </Card>
  );
}
